import React from 'react';
import { motion } from 'framer-motion';
import { Code2, Shield, Cloud, Network, Database, Terminal, Globe } from 'lucide-react';

const Skills = () => {
  const skillCategories = [
    {
      title: 'Programming',
      icon: <Code2 size={20} />,
      skills: ['TypeScript', 'Node.js', 'Python', 'C++', 'Java', 'React.js', 'Bash/Shell', 'SQL'],
      accent: 'blue'
    },
    {
      title: 'Cybersecurity',
      icon: <Shield size={20} />,
      skills: ['Metasploit', 'Nmap', 'Wireshark', 'Burp Suite', 'OSINT', 'Penetration Testing', 'Vulnerability Assessment'],
      accent: 'red'
    },
    {
      title: 'Cloud & DevOps',
      icon: <Cloud size={20} />,
      skills: ['AWS', 'Cloudflare', 'GCP', 'Docker', 'Containerization', 'CI/CD', 'Serverless'],
      accent: 'purple'
    },
    {
      title: 'Networking',
      icon: <Network size={20} />,
      skills: ['TCP/IP', 'Linux Admin', 'Kali Linux', 'VPN/Tunneling', 'Network Security', 'Firewalls'],
      accent: 'green'
    }
  ];

  return (
    <section id="skills" className="py-24 bg-gray-50 dark:bg-[#1c1c1e] transition-colors duration-300">
      <div className="container mx-auto px-6 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 dark:text-white mb-4 font-inter">
            Technical Expertise
          </h2>
          <p className="text-lg text-gray-500 dark:text-gray-400 max-w-2xl mx-auto font-inter">
            A comprehensive overview of my technical stack and security arsenal.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {skillCategories.map((category, idx) => (
            <motion.div
              key={category.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1, duration: 0.5 }}
              className="apple-card p-8 group hover:border-blue-500/30 dark:hover:border-blue-400/30 transition-all duration-300"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-xl bg-gray-50 dark:bg-gray-800 text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform duration-300">
                  {category.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white font-inter">
                  {category.title}
                </h3>
              </div>

              <div className="flex flex-wrap gap-2">
                {category.skills.map((skill) => (
                  <span 
                    key={skill}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-white dark:bg-black border border-gray-100 dark:border-gray-800 text-gray-600 dark:text-gray-400 font-inter hover:text-blue-600 dark:hover:text-blue-400 hover:border-blue-100 dark:hover:border-blue-900/30 transition-colors cursor-default"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Professional Certifications / Tools Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          <div className="apple-card p-6 flex items-center gap-4">
            <div className="p-3 rounded-2xl bg-blue-50 dark:bg-blue-900/20 text-blue-600">
              <Terminal size={24} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">15+</p>
              <p className="text-xs text-gray-500 uppercase tracking-widest font-bold">Security Tools</p>
            </div>
          </div>
          <div className="apple-card p-6 flex items-center gap-4">
            <div className="p-3 rounded-2xl bg-purple-50 dark:bg-purple-900/20 text-purple-600">
              <Database size={24} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">8+</p>
              <p className="text-xs text-gray-500 uppercase tracking-widest font-bold">Languages</p>
            </div>
          </div>
          <div className="apple-card p-6 flex items-center gap-4">
            <div className="p-3 rounded-2xl bg-green-50 dark:bg-green-900/20 text-green-600">
              <Globe size={24} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">30+</p>
              <p className="text-xs text-gray-500 uppercase tracking-widest font-bold">Certifications</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;
